import { useCallback, useEffect, useRef, useState } from 'react';

import { TABLE_MAINTENANCE_DATA } from '@/features/common/components/dialogs/dialogConstants';
import { useCommonQueryParams } from '@/features/common/hooks/useCommonQueryParams';
import { useUserSettings } from '@/features/common/hooks/useUserSettings';
import { getPresetRange, toIsoRange } from '@/features/maintenance-data/utils/dueAtPresets';
import { ComponentType, MaintenanceDataSettings, MaintenanceStatus } from '@/generated/GeneratedApiTypes';
import { MaintenanceDataRequestParams, MaintenanceSortBy } from '@/services/vehicleDiagnosticsApi';

export type DueAtRangeType = NonNullable<MaintenanceDataSettings['filters']['preset_date']>;
export type DueRangesOperator = NonNullable<MaintenanceDataRequestParams['dueRangesOperator']>;

export const useMaintenanceDataQueryParams = ({
    pageNumber,
    pageSize,
    assetIds,
    workshopIds,
    accountIds,
}: Partial<MaintenanceDataRequestParams>) => {
    const { settings, saveSettings, isSettingsError } =
        useUserSettings<MaintenanceDataSettings>(TABLE_MAINTENANCE_DATA);
    const settingsReady = useRef(false);

    const [status, setStatus] = useState<MaintenanceStatus[]>([]);
    const [componentType, setComponentType] = useState<ComponentType[]>([]);
    const [component, setComponent] = useState<string[]>([]);
    const [dueDateFrom, setDueDateFrom] = useState<string | undefined>(undefined);
    const [dueDateTo, setDueDateTo] = useState<string | undefined>(undefined);
    const [dueAtRangeType, setDueAtRangeType] = useState<DueAtRangeType | undefined>(undefined);
    const [dueMileageMin, setDueMileageMin] = useState<number | undefined>(undefined);
    const [dueMileageMax, setDueMileageMax] = useState<number | undefined>(undefined);
    const [mileageMin, setMileageMin] = useState<number | undefined>(undefined);
    const [mileageMax, setMileageMax] = useState<number | undefined>(undefined);
    const [dueRangesOperator, setDueRangesOperator] = useState<DueRangesOperator>('AND');
    const [activeMaintenance, setActiveMaintenance] = useState<boolean>(false);
    const [solvedByCustomer, setSolvedByCustomer] = useState<boolean>(false);
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [sortBy, setSortByState] = useState<MaintenanceSortBy>('vehicle');
    const [sortDirection, setSortDirectionState] = useState<'ASC' | 'DESC'>('ASC');

    useEffect(() => {
        if (settingsReady.current) return;
        if (!settings && !isSettingsError) return;
        const filters = settings?.filters;
        setStatus(filters?.maintenance_status ?? []);
        setComponentType(filters?.component_types ?? []);
        setComponent(filters?.maintenance_component ?? []);

        // Drift-awareness: a persisted computable preset (TODAY/NEXT_WEEK/NEXT_MONTH) is recomputed
        // relative to "now" so the stored dates never go stale; CUSTOM uses the persisted dates.
        const presetDate = filters?.preset_date ?? undefined;
        if (presetDate && presetDate !== 'CUSTOM') {
            const { from, to } = toIsoRange(getPresetRange(presetDate));
            setDueDateFrom(from);
            setDueDateTo(to);
        } else {
            setDueDateFrom(filters?.from_due_at ?? undefined);
            setDueDateTo(filters?.to_due_at ?? undefined);
        }
        setDueAtRangeType(presetDate);
        setDueMileageMin(filters?.from_due_in ?? undefined);
        setDueMileageMax(filters?.to_due_in ?? undefined);
        setMileageMin(filters?.mileage_range?.from_mileage ?? undefined);
        setMileageMax(filters?.mileage_range?.to_mileage ?? undefined);
        setActiveMaintenance(filters?.show_only_pro_active_maintenance ?? false);
        setSolvedByCustomer(filters?.solved_by_customer ?? false);
        setDueRangesOperator(filters?.use_date_or_mileage ? 'OR' : 'AND');
        setShowFilters(settings?.show_filters ?? false);
        setSortByState((settings?.sort?.sort_by as MaintenanceSortBy) ?? 'vehicle');
        setSortDirectionState(settings?.sort?.sort_direction ?? 'ASC');
        settingsReady.current = true;
    }, [settings, isSettingsError]);

    const commonQueryParams = useCommonQueryParams<MaintenanceSortBy>({
        sortBy,
        sortDirection,
        pageNumber,
        pageSize,
        assetIds,
        workshopIds,
        accountIds,
    });

    useEffect(() => {
        if (!settingsReady.current) {
            return;
        }
        saveSettings({
            show_filters: showFilters,
            filters: {
                maintenance_status: status,
                component_types: componentType,
                maintenance_component: component,
                from_due_at: dueDateFrom,
                to_due_at: dueDateTo,
                preset_date: dueAtRangeType,
                from_due_in: dueMileageMin,
                to_due_in: dueMileageMax,
                mileage_range: { from_mileage: mileageMin, to_mileage: mileageMax },
                show_only_pro_active_maintenance: activeMaintenance,
                solved_by_customer: solvedByCustomer,
                use_date_or_mileage: dueRangesOperator === 'OR',
            },
            sort: {
                sort_by: commonQueryParams.sorting.sortBy ?? undefined,
                sort_direction: commonQueryParams.sorting.sortDirection ?? undefined,
            },
        } as Partial<MaintenanceDataSettings>);
    }, [
        status,
        componentType,
        component,
        dueDateFrom,
        dueDateTo,
        dueAtRangeType,
        dueMileageMin,
        dueMileageMax,
        mileageMin,
        mileageMax,
        activeMaintenance,
        solvedByCustomer,
        dueRangesOperator,
        showFilters,
        commonQueryParams.sorting.sortBy,
        commonQueryParams.sorting.sortDirection,
    ]);

    const resetFilters = useCallback(() => {
        setStatus([]);
        setComponentType([]);
        setComponent([]);
        setDueDateFrom(undefined);
        setDueDateTo(undefined);
        setDueAtRangeType(undefined);
        setDueMileageMin(undefined);
        setDueMileageMax(undefined);
        setMileageMin(undefined);
        setMileageMax(undefined);
        setDueRangesOperator('AND');
        setActiveMaintenance(false);
        setSolvedByCustomer(false);
    }, []);

    return {
        status,
        setStatus,
        componentType,
        setComponentType,
        component,
        setComponent,
        dueDateFrom,
        setDueDateFrom,
        dueDateTo,
        setDueDateTo,
        dueAtRangeType,
        setDueAtRangeType,
        dueMileageMin,
        setDueMileageMin,
        dueMileageMax,
        setDueMileageMax,
        mileageMin,
        setMileageMin,
        mileageMax,
        setMileageMax,
        dueRangesOperator,
        setDueRangesOperator,
        activeMaintenance,
        setActiveMaintenance,
        solvedByCustomer,
        setSolvedByCustomer,
        showFilters,
        setShowFilters,
        resetFilters,
        settingsReady: settingsReady.current,
        ...commonQueryParams,
    };
};
